import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Briefcase, Users } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const departmentOptions = [
  'Management', 'Sales', 'Production', 'Store', 'Purchase', 
  'HR', 'Admin', 'MIS', 'Design', 'Operations', 'Accounts'
];

export default function DesignationMasterPage() {
  const { getAuthHeader } = useAuth();
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDesignation, setEditDesignation] = useState(null);
  const [deleteDesignation, setDeleteDesignation] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    department: '',
    description: ''
  });

  const fetchDesignations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/designations`, { headers: getAuthHeader() });
      setDesignations(response.data);
    } catch (error) {
      toast.error('Failed to fetch designations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Designation name is required');
      return;
    }
    
    try {
      if (editDesignation) {
        await axios.patch(`${API_URL}/designations/${editDesignation.id}`, formData, { headers: getAuthHeader() });
        toast.success('Designation updated. Employee permissions synced automatically.');
      } else {
        await axios.post(`${API_URL}/designations`, formData, { headers: getAuthHeader() });
        toast.success('Designation created');
      }
      setOpenDialog(false);
      resetForm();
      fetchDesignations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save designation');
    }
  };

  const handleDelete = async () => {
    if (!deleteDesignation) return;
    
    try {
      await axios.delete(`${API_URL}/designations/${deleteDesignation.id}`, { headers: getAuthHeader() });
      toast.success('Designation deleted');
      setDeleteDesignation(null);
      fetchDesignations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete designation');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await axios.post(`${API_URL}/designations/seed-defaults`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
      fetchDesignations();
    } catch (error) {
      toast.error('Failed to seed default designations');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      department: '',
      can_see_financial_data: false,
      allowed_modules: [],
      description: ''
    });
    setEditDesignation(null);
  };

  const openEdit = (designation) => {
    setEditDesignation(designation);
    setFormData({
      name: designation.name || '',
      department: designation.department || '',
      can_see_financial_data: designation.can_see_financial_data || false,
      allowed_modules: designation.allowed_modules || [],
      description: designation.description || ''
    });
    setOpenDialog(true);
  };

  const toggleModule = (moduleKey) => {
    setFormData(prev => ({
      ...prev,
      allowed_modules: prev.allowed_modules.includes(moduleKey)
        ? prev.allowed_modules.filter(m => m !== moduleKey)
        : [...prev.allowed_modules, moduleKey]
    }));
  };

  const selectAllInGroup = (groupModules) => {
    const moduleKeys = groupModules.map(m => m.key);
    const allSelected = moduleKeys.every(k => formData.allowed_modules.includes(k));
    
    if (allSelected) {
      // Deselect all in group
      setFormData(prev => ({
        ...prev,
        allowed_modules: prev.allowed_modules.filter(m => !moduleKeys.includes(m))
      }));
    } else {
      // Select all in group
      setFormData(prev => ({
        ...prev,
        allowed_modules: [...new Set([...prev.allowed_modules, ...moduleKeys])]
      }));
    }
  };

  const selectAll = () => {
    const allModules = Object.values(moduleGroups).flat().map(m => m.key);
    if (formData.allowed_modules.length === allModules.length) {
      setFormData(prev => ({ ...prev, allowed_modules: [] }));
    } else {
      setFormData(prev => ({ ...prev, allowed_modules: allModules }));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Briefcase className="w-6 h-6" />
              Designation Master
            </h1>
            <p className="text-muted-foreground">
              Manage designations and their module permissions. Changes auto-sync to employees.
            </p>
          </div>
          <div className="flex gap-2">
            {designations.length === 0 && (
              <Button variant="outline" onClick={handleSeedDefaults}>
                <Wand2 className="w-4 h-4 mr-2" />
                Load Defaults
              </Button>
            )}
            <Button onClick={() => { resetForm(); setOpenDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Designation
            </Button>
          </div>
        </div>

        {/* Designations Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Designation</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {designations.map((designation) => (
                  <TableRow key={designation.id}>
                    <TableCell>
                      <div className="font-medium">{designation.name}</div>
                      {designation.description && (
                        <p className="text-xs text-muted-foreground">{designation.description}</p>
                      )}
                    </TableCell>
                    <TableCell>{designation.department || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(designation)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteDesignation(designation)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {designations.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No designations yet. Click &quot;Load Defaults&quot; to add standard designations or create your own.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onOpenChange={(val) => { setOpenDialog(val); if (!val) resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editDesignation ? 'Edit Designation' : 'Add Designation'}</DialogTitle>
              <DialogDescription>
                Define designation and assign module permissions. These permissions will apply to all employees with this designation.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Designation Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                      placeholder="e.g., SALES MANAGER"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departmentOptions.map(dept => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this designation"
                    rows={2}
                  />
                </div>

                {/* Note about permissions */}
                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Module and report permissions are now managed per user in <strong>Masters → User Management → Edit User → Permissions</strong>
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editDesignation ? 'Update' : 'Create'} Designation
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteDesignation} onOpenChange={(val) => !val && setDeleteDesignation(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Designation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{deleteDesignation?.name}&quot;? 
                This action cannot be undone. Employees with this designation must be reassigned first.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
